/**
 * Modale "Appeler le client".
 *
 * Centrée, avec gros bouton téléphone, sélecteur de numéro, note d'appel + résultat.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { X, Phone, User } from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import { useToast } from '../../../hooks/use-toast';
import { initials, initialsBg } from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESULTS = [
  { id: 'reached', label: 'Joint' },
  { id: 'no_answer', label: 'Non joint' },
  { id: 'callback', label: 'À rappeler' },
  { id: 'wrong_number', label: 'Mauvais numéro' },
];

export default function CallModal() {
  const { modal, client, close } = useClientActions();
  const { toast } = useToast();
  const isOpen = modal === 'call' && !!client;

  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState('');
  const [calling, setCalling] = useState(false);

  const phones = React.useMemo(() => {
    const list = [client?.primaryPhone, client?.secondaryContact?.phone].filter(Boolean);
    if (list.length === 0) list.push('+225 07 00 00 00');
    return list;
  }, [client]);
  const [selectedPhone, setSelectedPhone] = useState(phones[0]);

  React.useEffect(() => {
    setSelectedPhone(phones[0]);
  }, [phones]);

  if (!isOpen) return null;

  const handleCall = async () => {
    setCalling(true);
    try {
      await axios.post(`${API}/admin/customer-success/actions`, {
        companyName: client.companyName,
        clientEmail: client.primaryEmail,
        action: 'call',
        channel: 'phone',
        body: note || null,
        result: outcome || null,
        visibility: 'commercial',
        metadata: { phone: selectedPhone },
      });
      toast({ title: 'Appel lancé', description: `Appel en cours vers ${client.companyName} (${selectedPhone}).` });
      close();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setCalling(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-testid="call-modal">
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <User className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Appeler le client</h2>
              <p className="text-xs text-gray-500">Appelez directement {client.companyName}</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="close-call-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-4 space-y-5 text-center">
          {/* Client info */}
          <div className="flex flex-col items-center gap-2">
            <div className={`w-12 h-12 rounded-full ${initialsBg(client.companyName)} text-white flex items-center justify-center text-sm font-semibold`}>
              {initials(client.companyName)}
            </div>
            <p className="text-sm font-semibold text-gray-900">{client.companyName}</p>
            <p className="text-xs text-gray-500">{selectedPhone}</p>
          </div>

          {/* Phone button */}
          <div className="flex justify-center">
            <button
              onClick={handleCall}
              disabled={calling}
              className={`relative w-24 h-24 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-105 ${calling ? 'animate-pulse' : ''}`}
              data-testid="call-launch-btn-big"
              aria-label="Lancer l'appel"
            >
              <span className="absolute inset-0 rounded-full ring-4 ring-emerald-100 animate-ping opacity-50" />
              <Phone className="w-10 h-10 relative" />
            </button>
          </div>

          <p className="text-xs text-gray-500">Appel via votre ligne professionnelle</p>

          {/* Phone select */}
          <div className="text-left">
            <label className="text-xs text-gray-500 block mb-1">Votre numéro</label>
            <Select value={selectedPhone} onValueChange={setSelectedPhone}>
              <SelectTrigger data-testid="call-phone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {phones.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Note d'appel */}
          <div className="text-left">
            <label className="text-xs text-gray-500 block mb-1">Note d'appel (optionnel)</label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ajoutez du contexte avant l'appel..."
              data-testid="call-note"
            />
          </div>

          {/* Résultat (à compléter après appel) */}
          <div className="text-left">
            <label className="text-xs text-gray-500 block mb-1">Résultat (à renseigner après l'appel)</label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger data-testid="call-outcome">
                <SelectValue placeholder="Sélectionner un résultat..." />
              </SelectTrigger>
              <SelectContent>
                {RESULTS.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={close} data-testid="call-cancel-btn">Annuler</Button>
          <Button
            size="sm" onClick={handleCall} disabled={calling}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="call-launch-btn"
          >
            <Phone className="w-3.5 h-3.5 mr-1.5" />
            {calling ? 'Appel...' : "Lancer l'appel"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
